import React, { useState, useMemo } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
} from "recharts";
import { BarChart as BarIcon, LineChart as LineIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Download } from "lucide-react";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";

const MarketLabChartTemplate = ({
  title,
  description,
  data,
  footerText,
  showControls = true,
}) => {
  const [selectedChartType, setSelectedChartType] = useState("line");
  const [showCumulative, setShowCumulative] = useState(false);
  const [timeframe, setTimeframe] = useState("day");

  // Process data for different timeframes
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    if (timeframe === "week") {
      // Group data by weeks - use LAST DATE value of each week (not sum)
      const weeklyData = new Map();

      data.forEach((item, index) => {
        const weekIndex = Math.floor(index / 7);
        const weekKey = `Week ${weekIndex + 1}`;

        if (!weeklyData.has(weekKey)) {
          weeklyData.set(weekKey, {
            date: weekKey,
            name: weekKey,
            value: item.value, // Initialize with first value
            count: 1,
          });
        } else {
          // Update with current value (so last iteration will have the last day's value)
          const weekData = weeklyData.get(weekKey);
          weekData.value = item.value; // Replace with current value (last day wins)
          weekData.count += 1;
        }
      });

      return Array.from(weeklyData.values());
    }

    return data; // Daily data
  }, [data, timeframe]);

  // Add cumulative data if enabled
  const chartData = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];

    let cumulativeValue = 0;
    return processedData.map((item) => {
      cumulativeValue += item.value;
      return {
        ...item,
        cumulativeValue: showCumulative ? cumulativeValue : undefined,
      };
    });
  }, [processedData, showCumulative]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return { total: 0, average: 0, peak: 0, dataPoints: 0 };
    }

    const total = chartData.reduce((sum, item) => sum + item.value, 0);
    const average = total / chartData.length;
    const peak = Math.max(...chartData.map((item) => item.value));

    return {
      total,
      average: Math.round(average),
      peak,
      dataPoints: chartData.length,
    };
  }, [chartData]);

  // Export to Excel functionality
  const exportToExcel = () => {
    if (!chartData || chartData.length === 0) {
      toast.error("No data to export.");
      return;
    }

    const exportData = chartData.map((item) => ({
      Period: item[dataKey],
      Value: item.value,
      ...(showCumulative && { "Cumulative Value": item.cumulativeValue }),
    }));

    // Add summary row
    exportData.push({
      Period: "SUMMARY",
      Value: "",
      ...(showCumulative && { "Cumulative Value": "" }),
    });
    exportData.push({
      Period: "Total",
      Value: summaryStats.total,
      ...(showCumulative && { "Cumulative Value": summaryStats.total }),
    });
    exportData.push({
      Period: "Average",
      Value: summaryStats.average,
      ...(showCumulative && { "Cumulative Value": "" }),
    });
    exportData.push({
      Period: "Peak",
      Value: summaryStats.peak,
      ...(showCumulative && { "Cumulative Value": "" }),
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Add title
    XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: "A1" });

    // Add metadata
    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Chart Type", selectedChartType],
        ["Timeframe", timeframe === "week" ? "Weekly" : "Daily"],
        ["Cumulative", showCumulative ? "Yes" : "No"],
        ["Generated", new Date().toLocaleString()],
        [""], // Empty row
      ],
      { origin: "A2" }
    );

    XLSX.utils.book_append_sheet(wb, ws, "Chart Data");
    XLSX.writeFile(
      wb,
      `${title.toLowerCase().replace(/\s+/g, "_")}_${
        new Date().toISOString().split("T")[0]
      }.xlsx`
    );
    toast.success("Data exported successfully!");
  };

  const formatValue = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  };

  // Determine the correct dataKey based on data structure
  const getDataKey = () => {
    if (chartData.length > 0) {
      return chartData[0].date !== undefined ? "date" : "name";
    }
    return "name";
  };

  const dataKey = getDataKey();

  const LineChartComponent = () => (
    <div className="h-[500px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 20, right: 30, bottom: 100, left: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={dataKey}
            angle={-45}
            textAnchor="end"
            height={100}
            interval="preserveStartEnd"
            tick={{ fontSize: 11 }}
            tickFormatter={(value) => {
              // Format dates if they look like dates
              if (typeof value === "string" && value.includes("-")) {
                try {
                  const date = new Date(value);
                  if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    });
                  }
                } catch (e) {
                  // If date parsing fails, return original value
                }
              }
              return value;
            }}
          />
          <YAxis tickFormatter={formatValue} />
          <Tooltip
            formatter={(value, name) => [
              value.toLocaleString(),
              name === "value" ? "Value" : "Cumulative Value",
            ]}
            labelFormatter={(label) => {
              // Format tooltip label for dates
              if (typeof label === "string" && label.includes("-")) {
                try {
                  const date = new Date(label);
                  if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString("en-US", {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    });
                  }
                } catch (e) {
                  // If date parsing fails, return original label
                }
              }
              return label;
            }}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
            name="Value"
          />
          {showCumulative && (
            <Line
              type="monotone"
              dataKey="cumulativeValue"
              stroke="#ef4444"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
              name="Cumulative Value"
            />
          )}
          <Brush dataKey={dataKey} height={30} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );

  const BarChartComponent = () => (
    <div className="h-[500px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, bottom: 100, left: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={dataKey}
            angle={-45}
            textAnchor="end"
            height={100}
            interval="preserveStartEnd"
            tick={{ fontSize: 11 }}
            tickFormatter={(value) => {
              // Format dates if they look like dates
              if (typeof value === "string" && value.includes("-")) {
                try {
                  const date = new Date(value);
                  if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    });
                  }
                } catch (e) {
                  // If date parsing fails, return original value
                }
              }
              return value;
            }}
          />
          <YAxis tickFormatter={formatValue} />
          <Tooltip
            formatter={(value, name) => [
              value.toLocaleString(),
              name === "value" ? "Value" : "Cumulative Value",
            ]}
            labelFormatter={(label) => {
              // Format tooltip label for dates
              if (typeof label === "string" && label.includes("-")) {
                try {
                  const date = new Date(label);
                  if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString("en-US", {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    });
                  }
                } catch (e) {
                  // If date parsing fails, return original label
                }
              }
              return label;
            }}
          />
          <Legend />
          <Bar
            dataKey="value"
            fill="#3b82f6"
            radius={[4, 4, 0, 0]}
            name="Value"
          />
          {showCumulative && (
            <Bar
              dataKey="cumulativeValue"
              fill="#ef4444"
              radius={[4, 4, 0, 0]}
              name="Cumulative Value"
            />
          )}
          <Brush dataKey={dataKey} height={30} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  if (!data || data.length === 0) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            No chart data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <CardTitle>{title}</CardTitle>
            {description && (
              <p className="text-sm text-gray-600 mt-1">{description}</p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportToExcel}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Statistics */}
        {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="text-lg font-semibold text-blue-600">
              {summaryStats.total.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Total</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-green-600">
              {summaryStats.average.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Average</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-purple-600">
              {summaryStats.peak.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Peak</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-orange-600">
              {summaryStats.dataPoints}
            </div>
            <div className="text-sm text-gray-600">Data Points</div>
          </div>
        </div> */}

        {/* Controls */}
        {showControls && (
          <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg">
            {/* <div className="flex items-center space-x-2">
              <Switch
                id="show-cumulative"
                checked={showCumulative}
                onCheckedChange={setShowCumulative}
              />
              <Label htmlFor="show-cumulative">Show Cumulative</Label>
            </div> */}

            <div className="flex items-center gap-2">
              <Label>Group By:</Label>
              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Day</SelectItem>
                  <SelectItem value="week">Week</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label>Chart Type:</Label>
              <Select
                value={selectedChartType}
                onValueChange={setSelectedChartType}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="line">Line</SelectItem>
                  <SelectItem value="bar">Bar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Chart */}
        <Tabs value={selectedChartType} onValueChange={setSelectedChartType}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="line" className="flex gap-2 items-center">
              <LineIcon className="h-4 w-4" />
              Line Chart
            </TabsTrigger>
            <TabsTrigger value="bar" className="flex gap-2 items-center">
              <BarIcon className="h-4 w-4" />
              Bar Chart
            </TabsTrigger>
          </TabsList>

          <TabsContent value="line">
            <LineChartComponent />
          </TabsContent>

          <TabsContent value="bar">
            <BarChartComponent />
          </TabsContent>
        </Tabs>

        {footerText && (
          <div className="text-sm text-gray-600 text-center">{footerText}</div>
        )}
      </CardContent>
    </Card>
  );
};

export default MarketLabChartTemplate;
